import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useEnRouteJobs } from '@/hooks/useEnRouteJobs';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import { enRouteEta } from '@/utils/techProgress';

/**
 * Technicians driving to a job, with the ETA they typed in the Steddy app when they pressed
 * "On my way". Renders nothing while nobody is on the road.
 */
export function OnTheWayCard() {
  const navigate = useNavigate();
  const { timezone } = useCompanyLocale();
  const { data: jobs = [] } = useEnRouteJobs();

  // Re-evaluate "running late" every minute without waiting for a data change.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (jobs.length === 0) return null;

  const time = (value: string) => formatDocDate(value, 'h:mm a', timezone);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-info/10">
            <Navigation className="h-4 w-4 text-info" />
          </div>
          <div>
            <CardTitle className="text-base">On the way</CardTitle>
            <CardDescription className="text-xs">ETAs sent from the technician app</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-border">
          {jobs.map((job) => {
            const eta = enRouteEta(job, now);
            return (
              <li key={job.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-1 py-2.5 text-left transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold">
                      {job.techs?.name ?? 'Technician'}
                      <span className="font-normal text-muted-foreground"> → {job.customer_name ?? 'Customer'}</span>
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[job.job_number, job.customer_suburb].filter(Boolean).join(' · ')}
                      {job.en_route_at ? ` · left ${time(job.en_route_at)}` : ''}
                    </p>
                  </div>
                  {eta ? (
                    <div className="flex shrink-0 flex-col items-end gap-0.5">
                      <Badge variant={eta.late ? 'warning' : 'info'} className="tabular-nums">
                        {eta.late ? 'Late · ' : ''}ETA {time(eta.expectedAt.toISOString())}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        said {eta.minutes} min at {time(eta.sentAt)}
                      </span>
                    </div>
                  ) : (
                    <Badge variant="info" className="shrink-0">En route</Badge>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
